import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { adminApi } from '../../utils/api';
import Button from './shared/Button';
import Input from './shared/Input';
import Select from './shared/Select';
import Card from './shared/Card';
import Alert from './shared/Alert';
import Badge from './shared/Badge';


export default function AdminRAGSearch() {
    const [searchParams] = useSearchParams();
    const [query, setQuery] = useState(() => searchParams.get('q') || '');
    const [collectionId, setCollectionId] = useState(() => searchParams.get('collection_id') || '');
    const autoRanRef = useRef(false);
    const [topK, setTopK] = useState(5);
    const [collections, setCollections] = useState([]);
    const [results, setResults] = useState(null);
    const [searching, setSearching] = useState(false);
    const [error, setError] = useState(null);
    const [loadingCollections, setLoadingCollections] = useState(true);
    const [responseTime, setResponseTime] = useState(null);
    const [showConfig, setShowConfig] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [filters, setFilters] = useState([]);
    const [documents, setDocuments] = useState([]);
    const [selectedDocTitle, setSelectedDocTitle] = useState('');

    useEffect(() => { loadCollections(); }, []);

    useEffect(() => {
        if (!collectionId) {
            setDocuments([]);
            setSelectedDocTitle('');
            return;
        }
        const col = collections.find(c => c.id === collectionId);
        if (col?.vector_store === 'gemini_file_search') {
            loadDocuments(collectionId);
        } else {
            setDocuments([]);
            setSelectedDocTitle('');
        }
    }, [collectionId, collections]);

    const loadDocuments = async (colId) => {
        try {
            const res = await adminApi.listRAGDocuments(colId);
            setDocuments(res.documents || []);
        } catch (err) {
            setDocuments([]);
        }
    };

    const loadCollections = async () => {
        try {
            const res = await adminApi.listRAGCollections();
            setCollections(res.collections || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoadingCollections(false);
        }
    };

    useEffect(() => {
        if (autoRanRef.current) return;
        if (loadingCollections) return;
        const initialQuery = searchParams.get('q');
        if (!initialQuery) return;
        autoRanRef.current = true;
        runSearch(initialQuery, searchParams.get('collection_id') || '');
    }, [loadingCollections, searchParams]);

    const runSearch = async (q, colId) => {
        const text = (q || '').trim();
        if (!text) return;
        setSearching(true);
        setError(null);
        setResponseTime(null);
        try {
            const payload = { query: text, top_k: topK };
            if (colId) payload.collection_ids = [colId];
            const start = performance.now();
            const res = await adminApi.searchRAG(payload);
            setResponseTime(Math.round(performance.now() - start));
            setResults(res);
        } catch (err) {
            setError(err.message);
        } finally {
            setSearching(false);
        }
    };

    const handleSearch = async (e) => {
        e.preventDefault();
        if (!query.trim()) return;
        setSearching(true);
        setError(null);
        setResponseTime(null);
        try {
            const payload = {
                query: query.trim(),
                top_k: topK,
            };
            if (collectionId) {
                payload.collection_ids = [collectionId];
            }
            const activeFilters = filters.reduce((acc, f) => {
                if (f.key.trim() && f.value.trim()) acc[f.key.trim()] = f.value.trim();
                return acc;
            }, {});
            if (selectedDocTitle) {
                activeFilters.metadata_filter = `title="${selectedDocTitle}"`;
            }
            if (Object.keys(activeFilters).length > 0) {
                payload.filters = activeFilters;
            }
            const start = performance.now();
            const res = await adminApi.searchRAG(payload);
            setResponseTime(Math.round(performance.now() - start));
            setResults(res);
        } catch (err) {
            setError(err.message);
        } finally {
            setSearching(false);
        }
    };

    const collectionOptions = [
        { value: '', label: 'All Collections' },
        ...collections.map(c => ({ value: c.id, label: `${c.name} (${c.id})` })),
    ];

    return (
        <div>
            <div className="mb-6">
                <h1 className="text-lg font-semibold text-gray-900">RAG Search</h1>
                <p className="text-sm text-gray-500 mt-0.5">Test semantic search across collections</p>
            </div>

            {error && <Alert variant="error" onClose={() => setError(null)} className="mb-4">{error}</Alert>}

            <Card title="Search Query" className="mb-4">
                <form onSubmit={handleSearch} className="space-y-3">
                    <Input
                        label="Query"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Enter your search query..."
                        required
                    />
                    <div className={`grid gap-4 ${documents.length > 0 ? 'grid-cols-3' : 'grid-cols-2'}`}>
                        <Select
                            label="Collection"
                            value={collectionId}
                            onChange={(e) => setCollectionId(e.target.value)}
                            options={collectionOptions}
                            disabled={loadingCollections}
                        />
                        {documents.length > 0 && (
                            <Select
                                label="File"
                                value={selectedDocTitle}
                                onChange={(e) => setSelectedDocTitle(e.target.value)}
                                options={[
                                    { value: '', label: 'All Files' },
                                    ...documents.map(d => ({ value: d.title, label: d.title })),
                                ]}
                            />
                        )}
                        <Input
                            label="Top K"
                            type="number"
                            value={topK}
                            onChange={(e) => setTopK(Math.max(1, Math.min(50, parseInt(e.target.value) || 5)))}
                            hint="1-50"
                        />
                    </div>
                    <div className="flex items-center justify-between">
                        <button
                            type="button"
                            onClick={() => {
                                setShowFilters(!showFilters);
                                if (!showFilters && filters.length === 0) {
                                    setFilters([{ key: '', value: '' }]);
                                }
                            }}
                            className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <svg className={`w-3 h-3 transition-transform ${showFilters ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                            Metadata Filters
                            {filters.filter(f => f.key && f.value).length > 0 && (
                                <Badge variant="info">{filters.filter(f => f.key && f.value).length}</Badge>
                            )}
                        </button>
                        <Button type="submit" loading={searching}>
                            Search
                        </Button>
                    </div>

                    {showFilters && (
                        <div className="space-y-2 border border-gray-100 rounded-md p-3 bg-gray-50/50">
                            {filters.map((filter, idx) => (
                                <div key={idx} className="flex items-center gap-2">
                                    <input
                                        type="text"
                                        value={filter.key}
                                        onChange={(e) => {
                                            const updated = [...filters];
                                            updated[idx] = { ...updated[idx], key: e.target.value };
                                            setFilters(updated);
                                        }}
                                        placeholder="key"
                                        className="h-8 flex-1 rounded border border-gray-200 px-2.5 text-xs text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-1"
                                    />
                                    <span className="text-xs text-gray-300">=</span>
                                    <input
                                        type="text"
                                        value={filter.value}
                                        onChange={(e) => {
                                            const updated = [...filters];
                                            updated[idx] = { ...updated[idx], value: e.target.value };
                                            setFilters(updated);
                                        }}
                                        placeholder="value"
                                        className="h-8 flex-1 rounded border border-gray-200 px-2.5 text-xs text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-1"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setFilters(filters.filter((_, i) => i !== idx))}
                                        className="text-gray-300 hover:text-red-400 transition-colors p-1"
                                    >
                                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>
                            ))}
                            <button
                                type="button"
                                onClick={() => setFilters([...filters, { key: '', value: '' }])}
                                className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                + Add filter
                            </button>
                        </div>
                    )}
                </form>
            </Card>

            {results && (
                <div className="space-y-3">
                    <div className="flex items-center justify-between">
                        <p className="text-sm text-gray-500">
                            {results.total} result{results.total !== 1 ? 's' : ''} for "{results.query}"
                            {responseTime !== null && (
                                <span className="ml-2 text-xs text-gray-400">in {responseTime}ms</span>
                            )}
                        </p>
                        <div className="flex items-center gap-3">
                            {results.estimated_cost && !results.estimated_cost.is_free && (
                                <span className="text-xs text-gray-400">
                                    est. ${results.estimated_cost.cost_usd.toFixed(6)} ({results.estimated_cost.model})
                                </span>
                            )}
                            {results.configs?.length > 0 && (
                                <button
                                    onClick={() => setShowConfig(!showConfig)}
                                    className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                    <svg className={`w-3 h-3 transition-transform ${showConfig ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                    Config
                                </button>
                            )}
                        </div>
                    </div>

                    {showConfig && results.configs?.length > 0 && (
                        <div className="border border-gray-100 rounded-md overflow-x-auto">
                            <table className="w-full text-xs">
                                <thead>
                                    <tr className="bg-gray-50/80">
                                        <th className="text-left px-3 py-1.5 font-medium text-gray-500">Collection</th>
                                        <th className="text-left px-3 py-1.5 font-medium text-gray-500">Vector Store</th>
                                        <th className="text-left px-3 py-1.5 font-medium text-gray-500">Embedding</th>
                                        <th className="text-left px-3 py-1.5 font-medium text-gray-500">Reranker</th>
                                        <th className="text-left px-3 py-1.5 font-medium text-gray-500">Chunks</th>
                                        <th className="text-left px-3 py-1.5 font-medium text-gray-500">Threshold</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50">
                                    {results.configs.map(c => (
                                        <tr key={c.collection_id}>
                                            <td className="px-3 py-1.5 text-gray-700 font-medium">{c.collection_name}</td>
                                            <td className="px-3 py-1.5 text-gray-600">{c.vector_store}</td>
                                            <td className="px-3 py-1.5">
                                                <span className="text-gray-600">{c.embedding_model}</span>
                                                <span className="text-gray-400 ml-1">({c.embedding_provider})</span>
                                            </td>
                                            <td className="px-3 py-1.5">
                                                {c.reranker_provider === 'none' ? (
                                                    <span className="text-gray-300">off</span>
                                                ) : (
                                                    <>
                                                        <span className="text-gray-600">{c.reranker_model}</span>
                                                        <span className="text-gray-400 ml-1">({c.reranker_provider})</span>
                                                    </>
                                                )}
                                            </td>
                                            <td className="px-3 py-1.5 text-gray-600">{c.chunk_size}/{c.chunk_overlap}</td>
                                            <td className="px-3 py-1.5 text-gray-600">{c.score_threshold ?? '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {(() => {
                        const responseText = results.results.find(r => r.metadata?.response_text)?.metadata?.response_text;
                        if (responseText) {
                            return (
                                <Card className="border-blue-200 bg-blue-50/30">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Badge variant="info">AI Response</Badge>
                                        <span className="text-xs text-gray-400">Generated from File Search Store</span>
                                    </div>
                                    <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                                        {responseText}
                                    </div>
                                </Card>
                            );
                        }
                        return null;
                    })()}

                    {results.results.length === 0 ? (
                        <div className="text-center py-8 text-gray-500 text-sm">
                            No results found. Try a different query or check your collections.
                        </div>
                    ) : (
                        results.results.map((result, idx) => (
                            <Card key={result.chunk_id || idx} className="hover:border-gray-300 transition-colors">
                                <div className="flex items-start justify-between gap-4 mb-2">
                                    <div className="flex items-center gap-2">
                                        <Badge variant="info">#{idx + 1}</Badge>
                                        <Badge variant="outline">{result.collection_id}</Badge>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs text-gray-500">Score</span>
                                        <Badge variant={result.score >= 0.7 ? 'success' : result.score >= 0.4 ? 'warning' : 'default'}>
                                            {(result.score * 100).toFixed(1)}%
                                        </Badge>
                                    </div>
                                </div>
                                <div className="bg-gray-50 rounded-md p-3 mb-2">
                                    <pre className="text-xs text-gray-700 whitespace-pre-wrap font-mono leading-relaxed">
                                        {result.content}
                                    </pre>
                                </div>
                                <div className="flex items-center gap-3 text-xs text-gray-400">
                                    <span>doc: {result.document_id?.slice(0, 8)}...</span>
                                    <span>chunk: {result.chunk_id?.slice(0, 8)}...</span>
                                    {result.metadata?.chunk_index !== undefined && (
                                        <span>index: {result.metadata.chunk_index}</span>
                                    )}
                                </div>
                            </Card>
                        ))
                    )}
                </div>
            )}
        </div>
    );
}
